import React, { useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Stack, Box, IconButton, Tooltip } from '@mui/material';
import { Info as InfoIcon } from '@mui/icons-material';
import { PhysicianDownloadCsv, PhysicianUploadCsv } from '../commands';
import { AddPhysicianDialog, PhysicianUploadHelpDialog } from '../dialogs';
import { PhysicianGrid } from '../grids';
import { useGetPhysiciansQuery } from '../../../../services/ConfigurationService';
import { UploadProgress } from '../../../../services/signalRService';

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

export const PhysiciansTab: React.FC = () => {
  const { id: centerId } = useParams<{ id: string }>();
  const query = useQuery();
  const company = query.get("company") || "";
  
  // Fetch physicians data
  const { data: physicians = [], refetch: refetchPhysicians } = useGetPhysiciansQuery({ company, center: centerId || '' }, { skip: !company || !centerId });
  
  // Upload progress state
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  
  // Help dialog state
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  
  // Upload handlers
  const handleUploadStart = () => {
    setProgress({
      operationId: '',
      progressPercentage: 0,
      message: 'Starting upload...',
      processedRecords: 0,
      totalRecords: 0,
      timestamp: new Date().toISOString(),
      status: 'Starting',
      uploadType: 'physicians'
    });
  };
  
  const handleProgressUpdate = (progress: UploadProgress) => {
    setProgress(progress);
  };
  
  const handleUploadComplete = (progress: UploadProgress) => {
    setProgress(progress);
  };
  
  const handleUploadError = (error: string) => {
    console.error('Physician upload error:', error);
    setProgress(null);
  };
  return (
    <Stack spacing={3}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 1 }}>
        <Tooltip title="View upload file specification">
          <IconButton 
            onClick={() => setHelpDialogOpen(true)}
            size="small"
            color="primary"
          >
            <InfoIcon />
          </IconButton>
        </Tooltip>
        
        <PhysicianDownloadCsv
          company={company}
          centerId={centerId || ''}
          onError={(error) => console.error('Download error:', error)}
        />
        <PhysicianUploadCsv
          company={company}
          centerId={centerId || ''}
          onUploadStart={handleUploadStart}
          onProgressUpdate={handleProgressUpdate}
          onComplete={handleUploadComplete}
          onSuccess={() => {
            refetchPhysicians();
            console.log('Physicians uploaded successfully');
          }}
          onError={handleUploadError}
        />
        <AddPhysicianDialog
          defaultCenterId={centerId || ''}
          defaultCompany={company}
          onAdd={refetchPhysicians}
        />
      </Box>
      <PhysicianGrid physicians={physicians} />
      
      {/* Help Dialog */}
      <PhysicianUploadHelpDialog 
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
      />
    </Stack>
  );
};